import { Component, OnInit, OnDestroy, Output, EventEmitter, inject } from '@angular/core';
import { FeatureAccessService } from '@app/services/feature-access.service';
import { projectInterface, ProjectsService } from 'src/app/services/project.service';
import { AngularFireAuth } from '@angular/fire/compat/auth';
import { Router, RouterModule, NavigationEnd } from '@angular/router';
import { CompanyService, companyInterface } from 'src/app/services/company.service';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatListModule } from '@angular/material/list';
import { MatDividerModule } from '@angular/material/divider';
import { MatMenuModule } from '@angular/material/menu';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatToolbarModule } from '@angular/material/toolbar';
import { LayoutService } from '../layout.service';
import { IndicatorComponent } from '@app/i3version/indicator/indicator.component';
import { Subject } from 'rxjs';
import { takeUntil, filter } from 'rxjs/operators';

@Component({
  selector: 'app-side-menu',
  templateUrl: './side-menu.component.html',
  styleUrls: ['./side-menu.component.scss'],
  providers: [ProjectsService, CompanyService],
  imports: [
    CommonModule,
    RouterModule,
    MatSidenavModule,
    MatToolbarModule,
    MatButtonModule,
    MatIconModule,
    MatListModule,
    MatDividerModule,
    MatMenuModule,
    MatProgressSpinnerModule,
    IndicatorComponent,
  ],
})
export class SideMenuComponent implements OnInit, OnDestroy {
  readonly featureAccess = inject(FeatureAccessService);

  @Output() clickOnVoice = new EventEmitter<void>();

  readonly projectsLimit = 5;

  projects: projectInterface[] = [];
  isLoadingProjects = false;
  isLoadingCompany = false;
  company?: companyInterface;
  companyId?: number;

  private destroy$ = new Subject<void>();

  constructor(
    public auth: AngularFireAuth,
    private router: Router,
    private companyService: CompanyService,
    private projectService: ProjectsService,
    private layoutService: LayoutService,
  ) {}

  ngOnInit(): void {
    // Primary source: layoutService (persists across route changes)
    this.layoutService.companyId$
      .pipe(takeUntil(this.destroy$))
      .subscribe((id) => {
        const newId = id !== undefined ? +id : undefined;
        if (newId && newId !== this.companyId) {
          this.companyId = newId;
          this.loadCompany();
          this.loadProjects();
        } else if (!newId) {
          this.companyId = undefined;
          this.company = undefined;
          this.projects = [];
        }
      });

    // Secondary: extract companyId from URL on each navigation
    this.router.events
      .pipe(
        filter((e) => e instanceof NavigationEnd),
        takeUntil(this.destroy$),
      )
      .subscribe(() => {
        const id = this.extractCompanyIdFromUrl(this.router.url);
        if (id && id !== this.companyId) {
          this.companyId = id;
          this.loadCompany();
          this.loadProjects();
        }
      });

    // Run once on init for current URL
    const id = this.extractCompanyIdFromUrl(this.router.url);
    if (id && id !== this.companyId) {
      this.companyId = id;
      this.loadCompany();
      this.loadProjects();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private extractCompanyIdFromUrl(url: string): number | undefined {
    // Routes like /:companyId/... — first segment after /
    const match = url.match(/^\/(\d+)\//);
    return match ? +match[1] : undefined;
  }

  private loadCompany(): void {
    if (!this.companyId) return;
    this.isLoadingCompany = true;
    this.companyService.getCompanyDetail(this.companyId, {}).subscribe({
      next: (res) => {
        this.company = res;
        this.isLoadingCompany = false;
        this.layoutService.companyName$.next(res.name);
      },
      error: () => {
        this.isLoadingCompany = false;
      },
    });
  }

  private loadProjects(): void {
    if (!this.companyId) return;
    this.isLoadingProjects = true;
    this.projectService.getProjectList(this.companyId).subscribe({
      next: (res) => {
        this.projects = res.results ?? [];
        this.isLoadingProjects = false;
      },
      error: () => {
        this.isLoadingProjects = false;
      },
    });
  }
}
